// LandingPage.jsx
import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Paper,
  useTheme,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { MessageCircle, Heart, Lock } from 'lucide-react';

// カスタムテーマの作成
const theme = createTheme({
  palette: {
    primary: {
      main: '#9c27b0', // パープル
      light: '#ba68c8',
      dark: '#7b1fa2',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
  },
});

const LandingPage = () => {
  // フィーチャーカードのスタイル
  const featureCardStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-8px)',
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f3e5f5 0%, #fff 100%)',
        pt: 8
      }}>
        {/* ヒーローセクション */}
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h1" color="primary" gutterBottom>
              あなたの声を届けよう
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={4}>
              匿名で質問を送れる、新しいコミュニケーションの形
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ 
                borderRadius: 8,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              始めてみる
            </Button>
          </Box>

          {/* 特徴セクション */}
          <Grid container spacing={4} mb={8}>
            {/* 匿名質問カード */}
            <Grid item xs={12} md={4}>
              <Card sx={featureCardStyle}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <MessageCircle size={48} color={theme.palette.primary.main} />
                    <Typography variant="h3" gutterBottom mt={2}>
                      匿名質問
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                      気になることを自由に質問。誰かの新しい一面を発見できるかもしれません。
                      あなたの好奇心が、新しい対話のきっかけを作ります。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* つながりを育むカード */}
            <Grid item xs={12} md={4}>
              <Card sx={featureCardStyle}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Heart size={48} color={theme.palette.primary.main} />
                    <Typography variant="h3" gutterBottom mt={2}>
                      つながりを育む
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                      質問と回答を通じて、より深い関係性を築けます。
                      一つの質問から始まる新しい発見の旅をお楽しみください。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* プライバシー重視カード */}
            <Grid item xs={12} md={4}>
              <Card sx={featureCardStyle}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Lock size={48} color={theme.palette.primary.main} />
                    <Typography variant="h3" gutterBottom mt={2}>
                      プライバシー重視
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                      安心して使えるよう、セキュリティとプライバシーを徹底的に守ります。
                      あなたの大切な対話を、しっかりと守ります。
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 使い方セクション */}
          <Box textAlign="center" mb={8}>
            <Typography variant="h2" color="primary" gutterBottom>
              使い方
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  step: 1,
                  title: 'アカウント作成',
                  description: '簡単な登録でマイページが作れます'
                },
                {
                  step: 2,
                  title: 'URLをシェア',
                  description: 'あなたの質問箱のURLを友達とシェア'
                },
                {
                  step: 3,
                  title: '質問に回答',
                  description: '届いた質問に自由に回答できます'
                }
              ].map((item) => (
                <Grid item xs={12} md={4} key={item.step}>
                  <Paper 
                    elevation={0}
                    sx={{
                      p: 3,
                      backgroundColor: 'transparent'
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 2
                      }}
                    >
                      <Typography variant="h3" color="white">
                        {item.step}
                      </Typography>
                    </Box>
                    <Typography variant="h3" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA セクション */}
          <Box textAlign="center" pb={8}>
            <Typography variant="h2" color="primary" gutterBottom>
              さっそく始めてみませんか？
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={4}>
              新しい発見と出会いがあなたを待っています
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{
                borderRadius: 8,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              無料で始める
            </Button>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;